import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { usePSIFlows, usePSIFlow, usePSIFlowStats, PSI_FLOW_QUERY_KEYS } from '@/hooks/usePSIFlow';
import { useQueryClient } from 'react-query';
import FlowListView from './FlowListView';
import ProcurementFlowView from './ProcurementFlowView';
import SalesFlowView from './SalesFlowView';
import FlowDetailDialog from './FlowDetailDialog';
import PurchaseOrderDialog from '../PurchaseOrderDialog';
import SalesQuotationDialog from '../SalesQuotationDialog';
import SalesOrderDialog from '../SalesOrderDialog';
import InvoiceDialog from '../InvoiceDialog';
import ReturnDialog from '../ReturnDialog';
import FlowButtonRenderer from '@/components/flowbutton/FlowButtonRenderer';
import type { FlowType, FlowStage, PSIFlowDetail } from '@/types/psiFlow';
import type { PurchaseOrder, POResponse } from '@/types/purchaseorder';
import type { SalesQuotation, SalesOrder, SQResponse, SOResponse } from '@/types/salesorder';
import type { InboundShipment } from '@/types/inbound';
import type { Invoice } from '@/types/invoice';
import type { Return } from '@/types/returns';
import type { IFlowButton } from '@/types/flowbutton';

// Test flow button for Part Create flow — hardcoded for testing
const TEST_PART_CREATE_BUTTON: IFlowButton = {
  id: 'test-part-create',
  name: 'Scan Part',
  subtitle: 'Extract & create',
  flow_type: 'part_create',
  flow_config: {
    default_category: 'electronic_parts',
    default_part_type: 'R',
    auto_generate_pn: true,
    enable_digikey_lookup: false,
    enable_batch_mode: false,
  },
  shape: 'pill',
  icon: 'scan-line',
  color: 'emerald',
  page: '/app/psi/flows',
  created_by: 'system',
  enabled: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

type ViewMode = 'list' | 'flow';
type EditableDocument = PurchaseOrder | SalesQuotation | SalesOrder | InboundShipment | Invoice | Return;
type EditingDocument = {
  stage: FlowStage;
  document: EditableDocument | null;
};

const PSIFlowContainer: React.FC = () => {
  const { t } = useTranslation('psi');
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [flowTypeFilter, setFlowTypeFilter] = useState<FlowType | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedFlowId, setSelectedFlowId] = useState<string | undefined>();

  // Dialog state for node clicks (detail view)
  const [dialogStage, setDialogStage] = useState<FlowStage | null>(null);
  const [dialogFlowDetail, setDialogFlowDetail] = useState<PSIFlowDetail | null>(null);

  // Edit dialog state
  const [editingDocument, setEditingDocument] = useState<EditingDocument | null>(null);

  // Read flow ID from URL query params on mount
  useEffect(() => {
    const flowId = searchParams.get('id');
    if (flowId) {
      setSelectedFlowId(flowId);
      setViewMode('flow');
    }
  }, [searchParams]);

  // Update URL when flow is selected
  const handleSelectFlow = (flowId: string | undefined) => {
    setSelectedFlowId(flowId);
    if (flowId) {
      setSearchParams({ id: flowId });
      setViewMode('flow');
    } else {
      searchParams.delete('id');
      setSearchParams(searchParams);
    }
  };

  const { data: flowsData, isLoading: flowsLoading } = usePSIFlows({
    type: flowTypeFilter || undefined,
    status: statusFilter || undefined,
  });

  const { data: flowDetail } = usePSIFlow(selectedFlowId);
  const { data: stats } = usePSIFlowStats();

  const flows = flowsData?.flows || [];

  // Handle node click - open dialog instead of navigating
  const handleNodeClick = (stage: FlowStage, detail: PSIFlowDetail) => {
    setDialogStage(stage);
    setDialogFlowDetail(detail);
  };

  // Close detail dialog
  const handleCloseDialog = () => {
    setDialogStage(null);
    setDialogFlowDetail(null);
  };

  // Handle edit - open appropriate edit dialog
  const handleEdit = (stage: FlowStage, document: EditableDocument) => {
    setEditingDocument({ stage, document });
    handleCloseDialog(); // Close detail dialog when opening edit dialog
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setEditingDocument(null);
  };

  // Handle successful edit - refresh data
  const handleEditSuccess = () => {
    queryClient.invalidateQueries(PSI_FLOW_QUERY_KEYS.all);
    setEditingDocument(null);
  };

  // Navigate to document page (legacy - kept for Open button)
  const handleOpenDocument = (type: string, id: string) => {
    window.open(`/app/psi/${type === 'po' ? 'purchases' : type === 'sq' || type === 'so' ? 'sales' : 'invoices'}?id=${id}`, '_self');
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{stats.total_flows}</p>
            <p className="text-xs text-blue-600 dark:text-blue-400">{t('flow.stats.total', 'Total Flows')}</p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
            <p className="text-2xl font-bold text-green-700 dark:text-green-300">{stats.completed}</p>
            <p className="text-xs text-green-600 dark:text-green-400">{t('flow.stats.completed', 'Completed')}</p>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
            <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">{stats.in_progress}</p>
            <p className="text-xs text-yellow-600 dark:text-yellow-400">{t('flow.stats.inProgress', 'In Progress')}</p>
          </div>
          <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
            <p className="text-2xl font-bold text-red-700 dark:text-red-300">{stats.overdue}</p>
            <p className="text-xs text-red-600 dark:text-red-400">{t('flow.stats.overdue', 'Overdue')}</p>
          </div>
        </div>
      )}

      {/* Filters + View Toggle */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
            <button
              onClick={() => setFlowTypeFilter('')}
              className={`px-3 py-1.5 text-sm ${
                flowTypeFilter === ''
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t('flow.filter.all', 'All')}
            </button>
            <button
              onClick={() => setFlowTypeFilter('procurement')}
              className={`px-3 py-1.5 text-sm border-l border-gray-300 dark:border-gray-600 ${
                flowTypeFilter === 'procurement'
                  ? 'bg-purple-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t('flow.filter.procurement', 'Procurement')}
            </button>
            <button
              onClick={() => setFlowTypeFilter('sales')}
              className={`px-3 py-1.5 text-sm border-l border-gray-300 dark:border-gray-600 ${
                flowTypeFilter === 'sales'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {t('flow.filter.sales', 'Sales')}
            </button>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
          >
            <option value="">{t('flow.filter.allStatuses', 'All Statuses')}</option>
            <option value="in_progress">{t('flow.filter.inProgress', 'In Progress')}</option>
            <option value="completed">{t('flow.filter.completed', 'Completed')}</option>
            <option value="overdue">{t('flow.filter.overdue', 'Overdue')}</option>
          </select>
        </div>

        {/* View Mode Toggle */}
        <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm ${
              viewMode === 'list'
                ? 'bg-indigo-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            {t('flow.view.list', 'List')}
          </button>
          <button
            onClick={() => setViewMode('flow')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border-l border-gray-300 dark:border-gray-600 ${
              viewMode === 'flow'
                ? 'bg-indigo-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {t('flow.view.flow', 'Flow')}
          </button>
        </div>
      </div>

      {viewMode === 'list' ? (
        <>
          {/* Flow List */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <FlowListView
              flows={flows}
              isLoading={flowsLoading}
              selectedFlowId={selectedFlowId}
              onSelectFlow={handleSelectFlow}
            />
          </div>
        </>
      ) : (
        <>
          {/* Flow Selector (compact) + React Flow View */}
          {flows.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                {t('flow.detail.selectFlow', 'Select Flow')}:
              </label>
              <select
                value={selectedFlowId || ''}
                onChange={(e) => handleSelectFlow(e.target.value || undefined)}
                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                <option value="">{t('flow.detail.chooseFlow', '-- Choose a flow --')}</option>
                {flows.map((flow) => (
                  <option key={flow.flow_id} value={flow.flow_id}>
                    {flow.doc_number || flow.flow_id.slice(0, 8)} - {flow.flow_type === 'procurement' ? t('flow.filter.procurement', 'Procurement') : t('flow.filter.sales', 'Sales')} - {flow.counterparty || '-'} ({flow.status?.replace(/_/g, ' ')})
                  </option>
                ))}
              </select>
            </div>
          )}

          {flowDetail && selectedFlowId ? (
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('flow.detail.title', 'Flow Detail')} - {flowDetail.flow_id.slice(0, 8)}...
                </h3>
                <button
                  onClick={() => handleSelectFlow(undefined)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {flowDetail.flow_type === 'procurement' ? (
                <ProcurementFlowView flowDetail={flowDetail} onOpenDocument={handleOpenDocument} onNodeClick={handleNodeClick} />
              ) : (
                <SalesFlowView flowDetail={flowDetail} onOpenDocument={handleOpenDocument} onNodeClick={handleNodeClick} />
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <p>{t('flow.detail.selectPrompt', 'Select a flow above to view its workflow diagram')}</p>
              </div>
            </div>
          )}
        </>
      )}

      {/* Flow Detail Dialog */}
      {dialogStage && dialogFlowDetail && (
        <FlowDetailDialog
          stage={dialogStage}
          flowDetail={dialogFlowDetail}
          onClose={handleCloseDialog}
          onEdit={handleEdit}
        />
      )}

      {/* Edit Dialogs */}
      {editingDocument?.stage === 'purchase_order' && editingDocument.document && (
        <PurchaseOrderDialog
          editingPO={editingDocument.document as POResponse}
          onClose={handleCloseEditDialog}
          onSuccess={handleEditSuccess}
        />
      )}

      {editingDocument?.stage === 'quotation' && editingDocument.document && (
        <SalesQuotationDialog
          editingSQ={editingDocument.document as SQResponse}
          onClose={handleCloseEditDialog}
          onSuccess={handleEditSuccess}
        />
      )}

      {editingDocument?.stage === 'sales_order' && editingDocument.document && (
        <SalesOrderDialog
          editingSO={editingDocument.document as SOResponse}
          onClose={handleCloseEditDialog}
          onSuccess={handleEditSuccess}
        />
      )}

      {editingDocument?.stage === 'invoice' && editingDocument.document && (
        <InvoiceDialog
          isOpen={true}
          invoice={editingDocument.document as Invoice}
          onClose={() => { handleCloseEditDialog(); handleEditSuccess(); }}
        />
      )}

      {editingDocument?.stage === 'return' && editingDocument.document && (
        <ReturnDialog
          isOpen={true}
          returnDoc={editingDocument.document as Return}
          onClose={() => { handleCloseEditDialog(); handleEditSuccess(); }}
        />
      )}
      {/* Test Part Create Flow Button (draggable) */}
      <FlowButtonRenderer button={TEST_PART_CREATE_BUTTON} readOnly />
    </div>
  );
};

export default PSIFlowContainer;
